import type {
  AppSettings,
  AutomationCredentials,
  AutomationEntity,
  AutomationExecutionPlan,
  AutomationFieldMapping,
  AutomationMappedUrl,
  AutomationProfile,
  AutomationRunLog,
  AutomationVerificationCheck,
  AutomationVerificationResult,
  AutomationWorkflowDefinition,
  AutomationWorkflowRun,
  AutomationWorkflowStep,
  AutomationWorkflowStepType,
} from '@/types'
import { apiFetch } from '@/lib/api'

import { API_BASE } from '@/lib/config'

type PersistedWorkflowRunPayload = {
  id?: string
  conversationId?: string
  sourceType: 'automation_profile' | 'trigger'
  sourceId: string
  workflowDefinitionId?: string
  workflowVersion: number
  status: string
  steps: AutomationWorkflowRun['steps']
  trigger?: AutomationWorkflowRun['trigger']
  verificationStatus?: AutomationWorkflowRun['verificationStatus']
  verificationSummary?: string
  createdAt?: string
  updatedAt: string
}

export type StudioWorkflowMatch = {
  profile: AutomationProfile
  workflow: AutomationWorkflowDefinition
  score: number
  reasons: string[]
}

export type StudioWorkflowResolution =
  | {
      mode: 'direct'
      match: StudioWorkflowMatch
      suggestions: StudioWorkflowMatch[]
    }
  | {
      mode: 'suggest'
      suggestions: StudioWorkflowMatch[]
    }
  | {
      mode: 'none'
      suggestions: []
    }

function conversationKey(conversationId: string | null | undefined): string | null {
  return conversationId ? `sine_automation_profiles_${conversationId}` : null
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/https?:\/\//g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

function tokenize(value: string | null | undefined): string[] {
  return normalizeText(value)
    .split(/[^a-z0-9æøå]+/i)
    .map(part => part.trim())
    .filter(part => part.length >= 3)
}

function getProfileIdentityKey(profile: Pick<AutomationProfile, 'systemName' | 'domain'>): string {
  return `${normalizeText(profile.systemName)}::${normalizeText(profile.domain)}`
}

function mergeUniqueStrings(...groups: Array<string[] | undefined>): string[] {
  return Array.from(
    new Set(
      groups.flatMap(group => group ?? []).map(item => item.trim()).filter(Boolean),
    ),
  )
}

function mergeExecutionPlans(
  existingPlans: AutomationExecutionPlan[] | undefined,
  nextPlans: AutomationExecutionPlan[] | undefined,
): AutomationExecutionPlan[] | undefined {
  const merged = [...(nextPlans ?? []), ...(existingPlans ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(plan => {
    const key = normalizeText(`${plan.name} ${plan.goal}`)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeWorkflowDefinitions(
  existingDefinitions: AutomationWorkflowDefinition[] | undefined,
  nextDefinitions: AutomationWorkflowDefinition[] | undefined,
): AutomationWorkflowDefinition[] | undefined {
  const merged = [...(nextDefinitions ?? []), ...(existingDefinitions ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(definition => {
    const key = normalizeText(`${definition.name} ${definition.goal}`)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeEntities(
  existingEntities: AutomationEntity[] | undefined,
  nextEntities: AutomationEntity[] | undefined,
): AutomationEntity[] | undefined {
  const merged = [...(nextEntities ?? []), ...(existingEntities ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(entity => {
    const key = normalizeText(entity.id || entity.name)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeCredentials(
  existingCredentials: AutomationCredentials | undefined,
  nextCredentials: AutomationCredentials | undefined,
): AutomationCredentials | undefined {
  return nextCredentials ?? existingCredentials
}

function mergeMappedUrls(
  existingUrls: AutomationMappedUrl[] | undefined,
  nextUrls: AutomationMappedUrl[] | undefined,
): AutomationMappedUrl[] | undefined {
  const merged = [...(nextUrls ?? []), ...(existingUrls ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(item => {
    const key = normalizeText(`${item.label} ${item.url}`)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeFormFields(
  existingFields: AutomationFieldMapping[] | undefined,
  nextFields: AutomationFieldMapping[] | undefined,
): AutomationFieldMapping[] | undefined {
  const merged = [...(nextFields ?? []), ...(existingFields ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(item => {
    const key = normalizeText(`${item.page ?? ''} ${item.label} ${item.selector ?? ''}`)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeVerificationChecks(
  existingChecks: AutomationVerificationCheck[] | undefined,
  nextChecks: AutomationVerificationCheck[] | undefined,
): AutomationVerificationCheck[] | undefined {
  const merged = [...(nextChecks ?? []), ...(existingChecks ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(item => {
    const key = normalizeText(`${item.label} ${item.method} ${item.target ?? ''}`)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeVerificationResults(
  existingResults: AutomationVerificationResult[] | undefined,
  nextResults: AutomationVerificationResult[] | undefined,
): AutomationVerificationResult[] | undefined {
  const merged = [...(nextResults ?? []), ...(existingResults ?? [])]
  const seen = new Set<string>()
  const result = merged.filter(item => {
    const key = normalizeText(`${item.label} ${item.method}`)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return result.length > 0 ? result : undefined
}

function mergeAutomationProfiles(existing: AutomationProfile, next: AutomationProfile): AutomationProfile {
  return normalizeAutomationProfile({
    ...existing,
    ...next,
    id: existing.id,
    systemName: next.systemName || existing.systemName,
    domain: next.domain || existing.domain,
    loginUrl: next.loginUrl || existing.loginUrl,
    summary: next.summary || existing.summary,
    mappedPaths: mergeUniqueStrings(existing.mappedPaths, next.mappedPaths),
    knownActions: mergeUniqueStrings(existing.knownActions, next.knownActions),
    matchKeywords: mergeUniqueStrings(existing.matchKeywords, next.matchKeywords),
    mappedUrls: mergeMappedUrls(existing.mappedUrls, next.mappedUrls),
    formFields: mergeFormFields(existing.formFields, next.formFields),
    verificationChecks: mergeVerificationChecks(existing.verificationChecks, next.verificationChecks),
    executionPlans: mergeExecutionPlans(existing.executionPlans, next.executionPlans),
    workflowDefinitions: mergeWorkflowDefinitions(existing.workflowDefinitions, next.workflowDefinitions),
    entities: mergeEntities(existing.entities, next.entities),
    runHistory: existing.runHistory ?? next.runHistory ?? [],
    savedCredentials: mergeCredentials(existing.savedCredentials, next.savedCredentials),
    notes: next.notes || existing.notes,
    updatedAt: next.updatedAt || new Date().toISOString(),
    createdAt: existing.createdAt || next.createdAt,
  })
}

function inferWorkflowStepType(
  step: string,
  preferredExecution: AutomationProfile['preferredExecution'],
): AutomationWorkflowStepType {
  const normalized = normalizeText(step)
  if (normalized.includes('søk') || normalized.includes('research') || normalized.includes('finn')) return 'web.search'
  if (normalized.includes('logg inn') || normalized.includes('login')) return 'browser.login'
  if (normalized.includes('åpne') || normalized.includes('gå til') || normalized.includes('naviger')) return 'browser.navigate'
  if (normalized.includes('fyll') || normalized.includes('skriv inn')) return 'browser.fill'
  if (normalized.includes('trykk') || normalized.includes('klikk') || normalized.includes('lagre')) return 'browser.click'
  if (normalized.includes('hent') || normalized.includes('les av') || normalized.includes('ekstraher')) return 'browser.extract'
  if (normalized.includes('bekreft') || normalized.includes('sjekk') || normalized.includes('verif')) return 'browser.verify'
  if (preferredExecution === 'api') return 'api.request'
  if (preferredExecution === 'mcp') return 'mcp.call'
  return 'generic'
}

function buildWorkflowStep(
  profile: AutomationProfile,
  step: string,
  index: number,
): AutomationWorkflowStep {
  const type = inferWorkflowStepType(step, profile.preferredExecution)
  return {
    id: `${profile.id}-workflow-step-${index + 1}`,
    type,
    title: step,
    description: step,
    toolHint: profile.preferredExecution === 'api'
      ? 'api'
      : profile.preferredExecution === 'mcp'
        ? 'mcp'
        : type.startsWith('browser') || type === 'web.search'
          ? 'browser'
          : 'none',
  }
}

function dedupeProfiles(profiles: AutomationProfile[]): AutomationProfile[] {
  const merged = new Map<string, AutomationProfile>()
  for (const rawProfile of profiles) {
    const profile = normalizeAutomationProfile(rawProfile)
    const key = getProfileIdentityKey(profile)
    const existing = merged.get(key)
    merged.set(key, existing ? mergeAutomationProfiles(existing, profile) : profile)
  }
  return Array.from(merged.values())
}

function defaultKnownActions(profile: AutomationProfile): string[] {
  const fromPaths = profile.mappedPaths
    .map(path => path.trim())
    .filter(Boolean)
    .slice(0, 4)
  if (fromPaths.length > 0) return fromPaths
  return [profile.summary]
}

function defaultExecutionPlans(profile: AutomationProfile): AutomationExecutionPlan[] {
  const planSteps = profile.mappedPaths.length > 0
    ? profile.mappedPaths
    : [
        'Logg inn i systemet',
        'Naviger til riktig del av systemet',
        'Utfør endringen',
        'Bekreft at oppdateringen ble lagret',
      ]

  return [
    {
      id: `${profile.id}-default-plan`,
      name: 'Standard flyt',
      goal: profile.summary,
      steps: planSteps,
    },
  ]
}

function defaultWorkflowDefinitions(profile: AutomationProfile): AutomationWorkflowDefinition[] {
  const plans = profile.executionPlans?.length ? profile.executionPlans : defaultExecutionPlans(profile)
  return plans.map((plan, index) => ({
    id: `${profile.id}-workflow-${index + 1}`,
    name: plan.name,
    goal: plan.goal,
    version: 1,
    trigger: {
      type: 'chat_message',
      description: 'Starter når brukeren beskriver ønsket handling i chatten.',
    },
    steps: plan.steps.map((step, stepIndex) => buildWorkflowStep(profile, step, stepIndex)),
  }))
}

function enrichWorkflowDefinitions(
  profile: AutomationProfile,
  definitions: AutomationWorkflowDefinition[] | undefined,
): AutomationWorkflowDefinition[] | undefined {
  if (!definitions?.length) return definitions

  const mappedUrls = profile.mappedUrls ?? []
  const formFields = profile.formFields ?? []
  const verificationChecks = profile.verificationChecks ?? []
  const loginUrl = profile.loginUrl

  return definitions.map(definition => {
    let navigateIndex = 0

    return {
      ...definition,
      steps: (definition.steps ?? []).map(step => {
        const nextStep: AutomationWorkflowStep = {
          ...step,
          input: step.input ? { ...step.input } : undefined,
        }

        if (step.type === 'browser.login') {
          nextStep.input = {
            ...(nextStep.input ?? {}),
            loginUrl: (nextStep.input as Record<string, unknown> | undefined)?.loginUrl ?? loginUrl,
          }
          if (loginUrl && !nextStep.description.toLowerCase().includes(loginUrl.toLowerCase())) {
            nextStep.description = `${step.description} Start fra ${loginUrl}.`
          }
        }

        if (step.type === 'browser.navigate') {
          const mappedUrl = mappedUrls[navigateIndex] ?? mappedUrls[0]
          if (mappedUrl) {
            navigateIndex += 1
            nextStep.input = {
              ...(nextStep.input ?? {}),
              url: (nextStep.input as Record<string, unknown> | undefined)?.url ?? mappedUrl.url,
              urlLabel: mappedUrl.label,
            }
            if (!nextStep.description.toLowerCase().includes(mappedUrl.url.toLowerCase())) {
              nextStep.description = `${step.description} Gå til ${mappedUrl.label}: ${mappedUrl.url}.`
            }
          }
        }

        if (step.type === 'browser.fill' && formFields.length > 0) {
          nextStep.input = {
            ...(nextStep.input ?? {}),
            fields: (nextStep.input as Record<string, unknown> | undefined)?.fields ?? formFields.map(field => ({
              label: field.label,
              selector: field.selector,
              page: field.page,
              valueSource: field.valueSource,
              kind: field.kind,
            })),
          }
          if (!step.description.toLowerCase().includes('felt')) {
            nextStep.description = `${step.description} Bruk kartlagte felter: ${formFields.map(field => field.label).join(', ')}.`
          }
        }

        if (step.type === 'browser.verify' && verificationChecks.length > 0) {
          nextStep.input = {
            ...(nextStep.input ?? {}),
            checks: (nextStep.input as Record<string, unknown> | undefined)?.checks ?? verificationChecks.map(check => ({
              label: check.label,
              method: check.method,
              target: check.target,
              successText: check.successText,
            })),
          }
          if (!step.description.toLowerCase().includes('verif')) {
            nextStep.description = `${step.description} Verifiser med: ${verificationChecks.map(check => check.label).join(', ')}.`
          }
        }

        return nextStep
      }),
    }
  })
}

function defaultMatchKeywords(profile: AutomationProfile): string[] {
  const parts = [
    profile.systemName,
    profile.domain,
    ...(profile.knownActions ?? []),
    ...(profile.mappedPaths ?? []),
  ]

  return Array.from(
    new Set(
      parts
        .flatMap(part => part.split(/[\s,/|:>.-]+/))
        .map(normalizeText)
        .filter(token => token.length >= 3),
    ),
  )
}

function defaultStatus(profile: AutomationProfile): AutomationProfile['status'] {
  if (profile.preferredExecution === 'user_browser') return 'needs_auth'
  if (profile.loginUrl || profile.mappedPaths.length > 0) return 'ready'
  return 'mapped'
}

function defaultAuthStrategy(profile: AutomationProfile): AutomationProfile['authStrategy'] {
  if (profile.preferredExecution === 'api' || profile.preferredExecution === 'mcp') return 'api'
  if (profile.preferredExecution === 'internal_browser') return profile.loginUrl ? 'session' : 'unknown'
  if (profile.preferredExecution === 'user_browser') return 'takeover'
  return 'unknown'
}

function defaultEntities(profile: AutomationProfile): AutomationEntity[] {
  const clinicMatches = `${profile.summary} ${profile.notes ?? ''}`.match(/(\d+)\s+klinikk/iu)
  const count = clinicMatches ? Number(clinicMatches[1]) : 0
  if (!count || count < 2) return []

  return Array.from({ length: count }, (_, index) => ({
    id: `${profile.id}-entity-${index + 1}`,
    name: `Klinikk ${index + 1}`,
    kind: 'clinic',
  }))
}

export function normalizeAutomationProfile(profile: AutomationProfile): AutomationProfile {
  const executionPlans = profile.executionPlans?.length ? profile.executionPlans : defaultExecutionPlans(profile)
  const workflowDefinitions = enrichWorkflowDefinitions(
    profile,
    profile.workflowDefinitions?.length ? profile.workflowDefinitions : defaultWorkflowDefinitions({ ...profile, executionPlans }),
  )

  const normalized: AutomationProfile = {
    ...profile,
    loginUrl: profile.loginUrl?.trim() || undefined,
    mappedPaths: (profile.mappedPaths ?? []).map(path => path.trim()).filter(Boolean),
    knownActions: (profile.knownActions ?? defaultKnownActions(profile)).map(action => action.trim()).filter(Boolean),
    matchKeywords: (profile.matchKeywords ?? defaultMatchKeywords(profile)).map(normalizeText).filter(Boolean),
    mappedUrls: (profile.mappedUrls ?? []).filter(item => item?.label && item?.url),
    formFields: (profile.formFields ?? []).filter(item => item?.label),
    verificationChecks: (profile.verificationChecks ?? []).filter(item => item?.label && item?.method),
    executionPlans,
    workflowDefinitions: workflowDefinitions?.map(definition => ({
      ...definition,
      version: definition.version ?? 1,
    })),
    entities: profile.entities?.length ? profile.entities : defaultEntities(profile),
    runHistory: (profile.runHistory ?? []).map(run => ({
      ...run,
      verificationResults: mergeVerificationResults(undefined, run.verificationResults),
      verificationStatus: run.verificationStatus ?? (run.status === 'success' ? 'not_required' : undefined),
    })),
    status: profile.status ?? defaultStatus(profile),
    authStrategy: profile.authStrategy ?? defaultAuthStrategy(profile),
    lastUsedAt: profile.lastUsedAt,
    notes: profile.notes?.trim() || undefined,
  }

  return normalized
}

function parseProfiles(raw: string | null | undefined): AutomationProfile[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as AutomationProfile[]
    return Array.isArray(parsed) ? dedupeProfiles(parsed) : []
  } catch {
    return []
  }
}

export function listConversationAutomationProfiles(conversationId: string | null | undefined): AutomationProfile[] {
  const key = conversationKey(conversationId)
  if (!key) return []
  return parseProfiles(localStorage.getItem(key))
}

function writeConversationProfiles(conversationId: string | null | undefined, profiles: AutomationProfile[]): AutomationProfile[] {
  const key = conversationKey(conversationId)
  if (!key) return []
  const deduped = dedupeProfiles(profiles)
  localStorage.setItem(key, JSON.stringify(deduped))
  return deduped
}

async function persistAutomationProfile(
  profile: AutomationProfile,
  isShared: boolean,
): Promise<void> {
  await apiFetch(`${API_BASE}/api/automation-profiles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...profile,
      conversationId: isShared ? undefined : profile.conversationId,
      isShared,
    }),
  })
}

async function persistAutomationWorkflowRun(
  profileId: string,
  payload: PersistedWorkflowRunPayload,
): Promise<void> {
  await apiFetch(`${API_BASE}/api/automation-profiles/${encodeURIComponent(profileId)}/workflow-runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function listPersistedAutomationWorkflowRuns(
  profileId: string,
  conversationId?: string | null,
): Promise<AutomationWorkflowRun[]> {
  const query = conversationId
    ? `?conversation_id=${encodeURIComponent(conversationId)}`
    : ''
  const res = await apiFetch(`${API_BASE}/api/automation-profiles/${encodeURIComponent(profileId)}/workflow-runs${query}`)
  if (!res.ok) return []
  const data = await res.json()
  return (data.runs ?? []) as AutomationWorkflowRun[]
}

async function deletePersistedAutomationProfile(profileId: string): Promise<void> {
  await apiFetch(`${API_BASE}/api/automation-profiles/${profileId}`, {
    method: 'DELETE',
  })
}

export function saveConversationAutomationProfile(
  conversationId: string | null | undefined,
  profile: AutomationProfile,
): AutomationProfile[] {
  const current = listConversationAutomationProfiles(conversationId)
  const normalized = normalizeAutomationProfile(profile)
  const identityKey = getProfileIdentityKey(normalized)
  const existing = current.find(item => getProfileIdentityKey(item) === identityKey || item.id === normalized.id)
  const merged = existing ? mergeAutomationProfiles(existing, normalized) : normalized
  const next = [merged, ...current.filter(item => item.id !== merged.id && getProfileIdentityKey(item) !== identityKey)]
  void persistAutomationProfile({ ...merged, conversationId: conversationId ?? undefined }, false).catch(() => {})
  return writeConversationProfiles(conversationId, next)
}

export function updateConversationAutomationProfile(
  conversationId: string | null | undefined,
  profileId: string,
  updater: (profile: AutomationProfile) => AutomationProfile,
): AutomationProfile[] {
  const current = listConversationAutomationProfiles(conversationId)
  let updatedProfile: AutomationProfile | null = null
  const next = current.map(profile => {
    if (profile.id !== profileId) return profile
    updatedProfile = normalizeAutomationProfile(updater(profile))
    return updatedProfile
  })
  if (updatedProfile) {
    void persistAutomationProfile({ ...updatedProfile, conversationId: conversationId ?? undefined }, false).catch(() => {})
  }
  return writeConversationProfiles(conversationId, next)
}

export function deleteConversationAutomationProfile(
  conversationId: string | null | undefined,
  profileId: string,
): AutomationProfile[] {
  const next = listConversationAutomationProfiles(conversationId).filter(item => item.id !== profileId)
  void deletePersistedAutomationProfile(profileId).catch(() => {})
  return writeConversationProfiles(conversationId, next)
}

export function listSharedAutomationProfiles(settings: AppSettings): AutomationProfile[] {
  return dedupeProfiles((settings.sharedAutomationProfiles ?? []).map(normalizeAutomationProfile))
}

export function saveSharedAutomationProfile(settings: AppSettings, profile: AutomationProfile): AutomationProfile[] {
  const current = listSharedAutomationProfiles(settings)
  const normalized = normalizeAutomationProfile({
    ...profile,
    conversationId: undefined,
  })
  const identityKey = getProfileIdentityKey(normalized)
  const existing = current.find(item => getProfileIdentityKey(item) === identityKey || item.id === normalized.id)
  const merged = existing ? mergeAutomationProfiles(existing, normalized) : normalized
  void persistAutomationProfile(merged, true).catch(() => {})
  return dedupeProfiles([merged, ...current.filter(item => item.id !== merged.id && getProfileIdentityKey(item) !== identityKey)])
}

export function removeSharedAutomationProfile(settings: AppSettings, profileId: string): AutomationProfile[] {
  void deletePersistedAutomationProfile(profileId).catch(() => {})
  return listSharedAutomationProfiles(settings).filter(item => item.id !== profileId)
}

export async function syncAutomationProfilesFromBackend(
  conversationId: string | null | undefined,
  settings: AppSettings,
): Promise<{ conversationProfiles: AutomationProfile[]; sharedProfiles: AutomationProfile[] }> {
  const localConversationProfiles = listConversationAutomationProfiles(conversationId)
  const localSharedProfiles = listSharedAutomationProfiles(settings)
  const [conversationRes, sharedRes] = await Promise.all([
    conversationId
      ? apiFetch(`${API_BASE}/api/automation-profiles?conversation_id=${encodeURIComponent(conversationId)}`)
      : Promise.resolve(null),
    apiFetch(`${API_BASE}/api/automation-profiles?shared_only=true`),
  ])

  const remoteConversationProfiles = conversationRes && conversationRes.ok
    ? dedupeProfiles(((await conversationRes.json()).profiles ?? []).filter((profile: AutomationProfile) => profile.conversationId === conversationId))
    : []
  const remoteSharedProfiles = sharedRes.ok
    ? dedupeProfiles((await sharedRes.json()).profiles ?? [])
    : []

  const conversationProfiles = dedupeProfiles([
    ...remoteConversationProfiles,
    ...localConversationProfiles,
  ])
  const sharedProfiles = dedupeProfiles([
    ...remoteSharedProfiles,
    ...localSharedProfiles,
  ])

  if (conversationId) {
    writeConversationProfiles(conversationId, conversationProfiles)
  }

  return { conversationProfiles, sharedProfiles }
}

function scoreProfile(task: string, profile: AutomationProfile): number {
  const query = normalizeText(task)
  if (!query) return 0

  let score = 0

  if (query.includes(normalizeText(profile.systemName))) score += 10
  if (query.includes(normalizeText(profile.domain))) score += 8
  if (profile.loginUrl && query.includes(normalizeText(profile.loginUrl))) score += 5

  for (const keyword of profile.matchKeywords ?? []) {
    if (keyword && query.includes(keyword)) score += 3
  }

  for (const action of profile.knownActions ?? []) {
    const normalizedAction = normalizeText(action)
    if (normalizedAction && query.includes(normalizedAction)) score += 4
  }

  for (const entity of profile.entities ?? []) {
    if (query.includes(normalizeText(entity.name))) score += 4
  }

  return score
}

function scoreWorkflow(task: string, profile: AutomationProfile, workflow: AutomationWorkflowDefinition): StudioWorkflowMatch {
  const query = normalizeText(task)
  const queryTokens = new Set(tokenize(task))
  const reasons: string[] = []
  let score = scoreProfile(task, profile)

  const workflowName = normalizeText(workflow.name)
  const workflowGoal = normalizeText(workflow.goal)

  if (workflowName && query.includes(workflowName)) {
    score += 8
    reasons.push(`match på workflow-navn: ${workflow.name}`)
  }

  if (workflowGoal && query.includes(workflowGoal)) {
    score += 4
    reasons.push(`match på workflow-mål: ${workflow.goal}`)
  }

  for (const token of tokenize(workflow.name)) {
    if (queryTokens.has(token)) score += 2
  }

  for (const token of tokenize(workflow.goal)) {
    if (queryTokens.has(token)) score += 1
  }

  for (const step of workflow.steps ?? []) {
    const stepLabel = `${step.title} ${step.description}`
    let matchedStepToken = false
    for (const token of tokenize(stepLabel)) {
      if (queryTokens.has(token)) {
        score += 0.5
        matchedStepToken = true
      }
    }
    if (matchedStepToken) reasons.push(`treff i workflow-steg: ${step.title}`)
  }

  return {
    profile,
    workflow,
    score,
    reasons: Array.from(new Set(reasons)).slice(0, 3),
  }
}

export function findRelevantAutomationProfiles(
  task: string,
  conversationId: string | null | undefined,
  settings: AppSettings,
): AutomationProfile[] {
  const conversationProfiles = listConversationAutomationProfiles(conversationId)
  const sharedProfiles = listSharedAutomationProfiles(settings)
  const seen = new Set<string>()

  const merged = [...conversationProfiles, ...sharedProfiles].filter(profile => {
    const key = `${normalizeText(profile.systemName)}::${normalizeText(profile.domain)}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return merged
    .map(profile => ({ profile, score: scoreProfile(task, profile) }))
    .filter(entry => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(entry => entry.profile)
}

export function findRelevantAutomationWorkflows(
  task: string,
  conversationId: string | null | undefined,
  settings: AppSettings,
): StudioWorkflowMatch[] {
  const profiles = findRelevantAutomationProfiles(task, conversationId, settings)
  return profiles
    .flatMap(profile => (profile.workflowDefinitions ?? []).map(workflow => scoreWorkflow(task, profile, workflow)))
    .filter(entry => entry.score > 0)
    .sort((a, b) => b.score - a.score)
}

export function resolveStudioWorkflowIntent(
  task: string,
  conversationId: string | null | undefined,
  settings: AppSettings,
): StudioWorkflowResolution {
  const suggestions = findRelevantAutomationWorkflows(task, conversationId, settings).slice(0, 3)
  if (suggestions.length === 0) {
    return { mode: 'none', suggestions: [] }
  }

  const [top, second] = suggestions
  const hasStrongNameHit = normalizeText(task).includes(normalizeText(top.workflow.name))
  const clearLead = top.score >= 8 && (!second || top.score - second.score >= 2)

  if (hasStrongNameHit || clearLead) {
    return {
      mode: 'direct',
      match: top,
      suggestions,
    }
  }

  return {
    mode: 'suggest',
    suggestions,
  }
}

function formatProfileForMemory(profile: AutomationProfile, matched: boolean): { key: string; value: string } {
  const plan = profile.executionPlans?.[0]
  const runState = profile.runHistory?.[0]
  return {
    key: matched
      ? `RELEVANT STUDIO-AUTOMASJON: ${profile.systemName}`
      : `Automasjon: ${profile.systemName}`,
    value: [
      `Domene: ${profile.domain}`,
      profile.loginUrl ? `Login: ${profile.loginUrl}` : null,
      `Status: ${profile.status ?? 'mapped'}`,
      `Foretrukket kjøring: ${profile.preferredExecution}`,
      profile.authStrategy ? `Auth: ${profile.authStrategy}` : null,
      `Oppsummering: ${profile.summary}`,
      profile.knownActions?.length ? `Kjente handlinger: ${profile.knownActions.join(', ')}` : null,
      profile.mappedPaths.length > 0 ? `Kartlagt bane: ${profile.mappedPaths.join(' -> ')}` : null,
      profile.mappedUrls?.length ? `Kartlagte URL-er: ${profile.mappedUrls.map(item => `${item.label}: ${item.url}`).join(', ')}` : null,
      profile.formFields?.length ? `Kartlagte felter: ${profile.formFields.map(item => item.label).join(', ')}` : null,
      profile.verificationChecks?.length ? `Verifisering: ${profile.verificationChecks.map(item => item.label).join(', ')}` : null,
      plan ? `Plan: ${plan.steps.join(' -> ')}` : null,
      profile.entities?.length ? `Enheter: ${profile.entities.map(entity => entity.name).join(', ')}` : null,
      profile.savedCredentials?.username ? `Lagret brukernavn: ${profile.savedCredentials.username}` : null,
      profile.savedCredentials?.password ? `Lagret passord: ${profile.savedCredentials.password}` : null,
      runState ? `Sist brukt: ${runState.summary}` : null,
      runState?.verificationSummary ? `Siste verifisering: ${runState.verificationSummary}` : null,
      profile.notes ? `Notater: ${profile.notes}` : null,
      matched ? 'Bruk denne profilen foerst hvis oppgaven matcher.' : null,
    ].filter(Boolean).join(' | '),
  }
}

export function buildAutomationMemoryEntries(
  conversationId: string | null | undefined,
  settings: AppSettings,
  task?: string,
): Array<{ key: string; value: string }> {
  const conversationProfiles = listConversationAutomationProfiles(conversationId)
  const sharedProfiles = listSharedAutomationProfiles(settings)
  const relevant = task ? findRelevantAutomationProfiles(task, conversationId, settings) : []
  const matchedIds = new Set(relevant.map(profile => profile.id))
  const merged = [...relevant, ...conversationProfiles, ...sharedProfiles].filter((profile, index, arr) => (
    arr.findIndex(candidate => candidate.id === profile.id) === index
  ))

  return merged.map(profile => formatProfileForMemory(profile, matchedIds.has(profile.id)))
}

export function buildAutomationPrefillPrompt(profile: AutomationProfile): string {
  const normalized = normalizeAutomationProfile(profile)
  const action = normalized.knownActions?.[0] ?? normalized.summary
  const entityHint = normalized.entities?.length
    ? `Ta hensyn til ${normalized.entities.length} lagrede enheter i denne profilen.`
    : 'Gjenbruk eksisterende login og kartlagt bane.'

  return [
    `Bruk den kartlagte ${normalized.systemName}-automatiseringen til å ${action.toLowerCase()}.`,
    entityHint,
    'Ikke kartlegg systemet på nytt med mindre noe faktisk har endret seg.',
  ].join(' ')
}

export function buildAutomationRunPrompt(
  profile: AutomationProfile,
  entity?: AutomationEntity,
): string {
  const normalized = normalizeAutomationProfile(profile)
  const action = normalized.knownActions?.[0] ?? normalized.summary
  const plan = normalized.executionPlans?.[0]
  const scope = entity
    ? `Kjor bare for ${entity.name}. Ikke oppdater andre enheter.`
    : normalized.entities?.length
      ? `Denne profilen har ${normalized.entities.length} lagrede enheter. Oppdater alle relevante enheter hvis ikke brukeren har bedt om noe annet.`
      : 'Kjor automatiseringen for den lagrede konteksten i denne chatten.'

  return [
    `Bruk den kartlagte ${normalized.systemName}-automatiseringen til a ${action.toLowerCase()}.`,
    scope,
    normalized.loginUrl ? `Start fra login eller systeminngang: ${normalized.loginUrl}.` : null,
    normalized.mappedUrls?.length ? `Gjenbruk disse URL-ene: ${normalized.mappedUrls.map(item => `${item.label}=${item.url}`).join(', ')}.` : null,
    normalized.formFields?.length ? `Kjente felter i flyten: ${normalized.formFields.map(item => item.label).join(', ')}.` : null,
    normalized.verificationChecks?.length ? `Verifiser med: ${normalized.verificationChecks.map(item => item.label).join(', ')}.` : null,
    normalized.savedCredentials?.username ? `Bruk lagret brukernavn: ${normalized.savedCredentials.username}.` : null,
    normalized.savedCredentials?.password ? `Bruk lagret passord: ${normalized.savedCredentials.password}.` : null,
    plan ? `Folg den lagrede planen: ${plan.steps.join(' -> ')}.` : null,
    'Gjenbruk lagret auth, systemkunnskap og kartlagt bane. Ikke kartlegg systemet pa nytt med mindre noe faktisk er endret eller blokkerer gjennomforingen.',
    'Marker ikke oppgaven som fullfort for du har faktisk bekreftet resultatet. Hvis du ikke kan bevise at endringen ble lagret eller gjennomfort, si tydelig at kjoringen ikke er verifisert ennå.',
  ].filter(Boolean).join(' ')
}

export function buildWorkflowRunPrompt(
  profile: AutomationProfile,
  workflow: AutomationWorkflowDefinition,
  entity?: AutomationEntity,
): string {
  const normalized = normalizeAutomationProfile(profile)
  const scope = entity
    ? `Kjør bare for ${entity.name}. Ikke oppdater andre enheter.`
    : normalized.entities?.length
      ? `Denne studio-agenten har ${normalized.entities.length} lagrede enheter. Oppdater bare relevante enheter for denne workflowen hvis ikke brukeren har bedt om noe annet.`
      : 'Kjør workflowen i lagret kontekst for denne studio-agenten.'

  return [
    `Bruk studio-agenten for ${normalized.systemName} og kjør workflowen "${workflow.name}".`,
    `Workflow-mål: ${workflow.goal}.`,
    scope,
    normalized.loginUrl ? `Start fra login eller systeminngang: ${normalized.loginUrl}.` : null,
    normalized.mappedUrls?.length ? `Gjenbruk disse URL-ene: ${normalized.mappedUrls.map(item => `${item.label}=${item.url}`).join(', ')}.` : null,
    normalized.savedCredentials?.username ? `Bruk lagret brukernavn: ${normalized.savedCredentials.username}.` : null,
    normalized.savedCredentials?.password ? `Bruk lagret passord: ${normalized.savedCredentials.password}.` : null,
    `Følg denne workflowen steg for steg: ${(workflow.steps ?? []).map(step => step.title).join(' -> ')}.`,
    'Hvis workflowen ikke kan kjøres trygt ennå, be kort om avklaring i stedet for å gjette.',
    'Hvis workflowen feiler eller krever takeover, forklar tydelig hva som stoppet og be om manuell hjelp via CTA i chatten.',
  ].filter(Boolean).join(' ')
}

type LogLike = { message: string; type: string; success?: boolean }

function buildVerificationEvidence(logs: LogLike[]): string[] {
  return logs
    .map(log => `${log.type} ${log.message}`.trim())
    .filter(Boolean)
    .slice(-20)
}

function buildWorkflowRunPayload(
  profile: AutomationProfile,
  logEntry: AutomationRunLog,
): PersistedWorkflowRunPayload {
  const definition = profile.workflowDefinitions?.[0]
  const steps = (definition?.steps ?? []).map(step => ({
    id: step.id,
    title: step.title,
    description: step.description,
    type: step.type,
    status:
      logEntry.status === 'failed'
        ? 'error'
        : logEntry.status === 'queued' || logEntry.status === 'context_reused'
          ? 'running'
          : logEntry.status === 'needs_auth' || logEntry.status === 'needs_input'
            ? 'running'
            : 'done',
    detail: logEntry.summary,
    input: step.input,
  }))

  return {
    id: logEntry.id,
    conversationId: profile.conversationId,
    sourceType: 'automation_profile',
    sourceId: profile.id,
    workflowDefinitionId: definition?.id,
    workflowVersion: definition?.version ?? 1,
    status:
      logEntry.status === 'failed'
        ? 'failed'
        : logEntry.status === 'needs_auth'
          ? 'needs_auth'
          : logEntry.status === 'needs_input'
            ? 'needs_input'
            : logEntry.status === 'queued' || logEntry.status === 'context_reused'
              ? 'planning'
              : logEntry.status === 'unverified'
                ? 'verifying'
                : 'ready',
    steps,
    trigger: definition?.trigger,
    verificationStatus: logEntry.verificationStatus,
    verificationSummary: logEntry.verificationSummary,
    createdAt: logEntry.createdAt,
    updatedAt: logEntry.createdAt,
  }
}

export function deriveAutomationVerification(
  profile: AutomationProfile,
  logs: LogLike[],
): {
  status: AutomationRunLog['verificationStatus']
  summary: string
  results: AutomationVerificationResult[]
} {
  const evidenceLines = buildVerificationEvidence(logs)
  const evidenceText = evidenceLines.join(' \n ').toLowerCase()
  const checks = profile.verificationChecks ?? []

  if (checks.length === 0) {
    return {
      status: 'manual_required',
      summary: 'Ingen strukturert verifisering er lagret for denne automasjonen ennå.',
      results: [],
    }
  }

  const results = checks.map<AutomationVerificationResult>(check => {
    if (check.method === 'manual') {
      return {
        label: check.label,
        method: check.method,
        status: 'manual_required',
        evidence: check.successText || check.target,
      }
    }

    const candidates = [
      check.target,
      check.successText,
      check.label,
      check.method === 'save_confirmation' ? 'lagret' : undefined,
      check.method === 'save_confirmation' ? 'saved' : undefined,
      check.method === 'save_confirmation' ? 'publisert' : undefined,
      check.method === 'url_change' ? 'http' : undefined,
    ]
      .filter(Boolean)
      .map(value => normalizeText(value))

    const matchingEvidence = evidenceLines.find(line => {
      const normalizedLine = normalizeText(line)
      return candidates.some(candidate => candidate && normalizedLine.includes(candidate))
    })

    return {
      label: check.label,
      method: check.method,
      status: matchingEvidence ? 'verified' : 'unverified',
      evidence: matchingEvidence,
    }
  })

  const hasManual = results.some(result => result.status === 'manual_required')
  const verifiedCount = results.filter(result => result.status === 'verified').length
  const allVerified = results.length > 0 && results.every(result => result.status === 'verified')

  if (allVerified) {
    return {
      status: 'verified',
      summary: `Verifisert med ${verifiedCount} av ${results.length} lagrede sjekker.`,
      results,
    }
  }

  if (hasManual && verifiedCount === 0 && !evidenceText) {
    return {
      status: 'manual_required',
      summary: 'Denne kjøringen trenger manuell bekreftelse før den kan regnes som fullført.',
      results,
    }
  }

  return {
    status: hasManual ? 'manual_required' : 'unverified',
    summary: verifiedCount > 0
      ? `Bare ${verifiedCount} av ${results.length} verifiseringssjekker ble bekreftet.`
      : 'Ingen av de lagrede verifiseringssjekkene ble bekreftet i denne kjøringen.',
    results,
  }
}

export function recordAutomationProfileRun(
  conversationId: string | null | undefined,
  settings: AppSettings,
  profileId: string,
  status: AutomationRunLog['status'],
  summary: string,
  options?: {
    entityId?: string
    entityName?: string
    scope?: 'all' | 'entity'
    verificationStatus?: AutomationRunLog['verificationStatus']
    verificationSummary?: string
    verificationResults?: AutomationVerificationResult[]
  },
): { conversationProfiles: AutomationProfile[]; sharedProfiles?: AutomationProfile[] } {
  const logEntry: AutomationRunLog = {
    id: `${profileId}-${Date.now()}`,
    status,
    summary,
    createdAt: new Date().toISOString(),
    entityId: options?.entityId,
    entityName: options?.entityName,
    scope: options?.scope ?? (options?.entityId ? 'entity' : 'all'),
    verificationStatus: options?.verificationStatus,
    verificationSummary: options?.verificationSummary,
    verificationResults: options?.verificationResults,
  }

  const conversationProfiles = listConversationAutomationProfiles(conversationId)
  const matchingConversation = conversationProfiles.find(profile => profile.id === profileId)
  if (matchingConversation) {
    const next = updateConversationAutomationProfile(conversationId, profileId, profile => ({
      ...profile,
      lastUsedAt: logEntry.createdAt,
      runHistory: [logEntry, ...(profile.runHistory ?? [])].slice(0, 6),
      updatedAt: logEntry.createdAt,
    }))
    const updatedProfile = next.find(profile => profile.id === profileId)
    if (updatedProfile) {
      void persistAutomationWorkflowRun(profileId, buildWorkflowRunPayload(updatedProfile, logEntry)).catch(() => {})
    }
    return { conversationProfiles: next }
  }

  const sharedProfiles = listSharedAutomationProfiles(settings)
  const matchingShared = sharedProfiles.find(profile => profile.id === profileId)
  if (matchingShared) {
    const nextSharedProfiles = sharedProfiles.map(profile => (
      profile.id === profileId
        ? normalizeAutomationProfile({
            ...profile,
            conversationId: undefined,
            lastUsedAt: logEntry.createdAt,
            runHistory: [logEntry, ...(profile.runHistory ?? [])].slice(0, 6),
            updatedAt: logEntry.createdAt,
          })
        : profile
    ))
    const updatedShared = nextSharedProfiles.find(profile => profile.id === profileId)
    if (updatedShared) {
      void persistAutomationProfile(updatedShared, true).catch(() => {})
      void persistAutomationWorkflowRun(profileId, buildWorkflowRunPayload(updatedShared, logEntry)).catch(() => {})
    }
    return {
      conversationProfiles,
      sharedProfiles: nextSharedProfiles,
    }
  }

  return { conversationProfiles }
}

export function createAutomationProfileId(systemName: string, domain: string): string {
  return `${slugify(systemName)}:${slugify(domain)}`
}
